import { FormItem } from '@models/FormItem'
import {
  SBAsyncSelect,
  SBAsyncSelectRef,
} from '@supplement-bacon/react-async-select'
import { initialQuery } from '@supplement-bacon/sb-table-filters'
import { Entity } from '@utils/Entities'
import { Alert, Button, Col, Flex, Form, Input, Row } from 'antd'
import { FC, useEffect, useRef, useState } from 'react'

interface Props {
  item: FormItem
  onFormItemSelected: (formItem?: FormItem) => void
}

const AssociatedItemSelection: FC<Props> = ({ item, onFormItemSelected }) => {
  const [formInstance] = Form.useForm()
  const [display, setDisplay] = useState<
    'display-current' | 'add-button' | 'new-selector'
  >('add-button')

  const campaign = Form.useWatch('campaign', formInstance)
  const campaign_name = Form.useWatch('campaign_name', formInstance)
  const form = Form.useWatch('form', formInstance)
  const formItem = Form.useWatch('formItem', formInstance)
  const formItem_question = Form.useWatch('formItem_question', formInstance)

  const formSelectRef = useRef<SBAsyncSelectRef>(null)
  const formItemSelectRef = useRef<SBAsyncSelectRef>(null)

  useEffect(() => {
    /**
     * Even though we're tempted to do this in onValuesChange of the <SBAsyncSelect /> or in the onValuesChange of the <Form />,
     * here is the only place where we can be sure that the campaign value has changed, and the most importantly, that the change is finalized.
     * The change being finalized means that the `initialQuery`of the <SBAsyncSelect /> is updated and the fetch can be done with the correct filters.
     */
    formSelectRef.current?.fetch()
  }, [campaign])

  useEffect(() => {
    /**
     * See comment above for explanation
     */
    formItemSelectRef.current?.fetch()
  }, [form])

  useEffect(() => {
    /**
     * `item` is not added as dependency as we want to check only on mount
     */
    if (!item.associatedItems || item.associatedItems.length === 0) {
      setDisplay('add-button')
    } else {
      setDisplay('display-current')
    }
  }, [])

  if (display === 'display-current') {
    return (
      <Alert
        showIcon
        type="info"
        title={
          <>
            Actuellement, la question{' '}
            <b>« {item.associatedItems?.[0].question} »</b> d'un formulaire
            précédent est sélectionnée pour affichage sous cette question.
          </>
        }
        action={
          <Button
            type="link"
            onClick={() => {
              // Reset current selection
              onFormItemSelected(undefined)
              // Access to change selection
              setDisplay('new-selector')
            }}
          >
            Modifier la sélection
          </Button>
        }
      />
    )
  }

  if (display === 'add-button') {
    return (
      <Button
        type="dashed"
        className="w-full"
        onClick={() => setDisplay('new-selector')}
      >
        Afficher la réponse d'une question sur une précédente campagne
      </Button>
    )
  }

  return (
    <Flex vertical gap={0}>
      <Form form={formInstance} layout="horizontal">
        <Form.Item name="campaign_name" noStyle hidden>
          <Input hidden />
        </Form.Item>
        <Form.Item name="formItem_question" hidden noStyle>
          <Input type={'hidden'} />
        </Form.Item>
        <Row gutter={7}>
          <Col span={6}>
            <Form.Item name="campaign">
              <SBAsyncSelect
                allowClear
                disabled={formItem !== undefined}
                placeholder="Campagne"
                entity={Entity.campaign}
                style={{ width: '100%' }}
                onChange={(_, option) => {
                  formInstance.setFieldValue(
                    'campaign_name',
                    option ? option.name : undefined
                  )
                }}
              />
            </Form.Item>
          </Col>

          <Col span={6}>
            <Form.Item name="form">
              <SBAsyncSelect
                allowClear
                ref={formSelectRef}
                entity={Entity.form}
                placeholder="Formulaire"
                style={{ width: '100%' }}
                disabled={!campaign || formItem !== undefined}
                initialQuery={{
                  ...initialQuery,
                  filters: {
                    ...initialQuery.filters,
                    campaigns: [campaign],
                    isLocked: [true],
                  },
                }}
              />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item name="formItem">
              <SBAsyncSelect
                allowClear
                disabled={!form}
                placeholder="Question"
                ref={formItemSelectRef}
                entity={Entity.formItem}
                style={{ width: '100%' }}
                searchFieldName="question"
                initialQuery={{
                  ...initialQuery,
                  filters: {
                    ...initialQuery.filters,
                    forms: [form],
                  },
                }}
                onChange={(_, option) => {
                  formInstance.setFieldValue(
                    'formItem_question',
                    option ? option.question : undefined
                  )
                  onFormItemSelected(option ? option.item : undefined)
                }}
              />
            </Form.Item>
          </Col>

          {formItem_question && campaign_name && (
            <Col span={24}>
              <Alert
                showIcon
                type="success"
                title={
                  <>
                    Pour chaque employé, nous rechercherons la réponse à la
                    question <b>« {formItem_question} »</b> sur la campagne{' '}
                    <b>« {campaign_name} »</b> et l'afficherons sous la
                    question.
                    <br />
                    En l'absence de résultat, aucun affichage ne sera effectué.
                  </>
                }
              />
            </Col>
          )}
        </Row>
      </Form>
    </Flex>
  )
}

export default AssociatedItemSelection
